import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import AnalyticsCard from "./AnalyticsCard";
import AnalyticsTable from "./AnalyticsTable";

import EmptyState from "../../../UI/State/EmptyState";
import TotalCumulLine from "./TotalCumulLine";
import CheckBox from "../../../UI/Button/CheckboxButton";

import * as Utils from "../../../Utils/AnalyticsUtils";

import { faChartLine, faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as AnalyticsConstant from "../../../../middleware/Constants/AnalyticsConstant";
import * as AnalyticsActions from "../../../../middleware/Store/Actions/AnalyticsActions";
import ErrorPanel, { ErrorSize } from "../../../UI/ErrorPanel/ErrorPanel";

import LogService from "../../../../middleware/CommonSdk/LogService";
const LOG_TAG = "APPLICATION_ANALYTICS";

const Analytics = (props) => {

    const intl = useIntl();

    const [period, setPeriod] = useState("currentweek");
    const [dcZone, setDcZone] = useState([]);

    const [exportableAnalytics, setExportableAnalytics] = useState();
    const [analytics, setAnalytics] = useState();

    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect appId, groupsMapping, period");
        if (props.appId && props.groupsMapping) {
            handleAnalyticsRefresh();
        }
    }, [props.appId, props.groupsMapping, period]);

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect dcZone");
        if (props.appId && props.groupsMapping) {
            Utils.debounce(handleAnalyticsRefresh, 1000);
        }
    }, [dcZone]);


    const handleAnalyticsRefresh = () => {
        LogService.trace(LOG_TAG, "handleAnalyticsRefresh");
        LogService.info(LOG_TAG, "handleAnalyticsRefresh start");
        setProgress(true);
        setErrorMessage("");

        let metricPeriodDate = Utils.getPeriodDate(period);
        props.getAnalytics(props.appId, metricPeriodDate.period, metricPeriodDate.fromDay, metricPeriodDate.toDay, dcZone)
            .then(data => {
                const analyticsData = Utils.consolidateAnalytics(data, props.groupsMapping);
                setAnalytics(analyticsData);

                const exportableAnalytics = Utils.prepareExportableAnalytics(data);
                setExportableAnalytics(exportableAnalytics);

                setProgress(false);
                setErrorMessage("");
            })
            .catch(errorPayload => {
                setProgress(false);
                if (errorPayload.errorCode.toString().startsWith("403"))
                    setErrorMessage(errorPayload.errorMessage);
                else
                    setErrorMessage(intl.messages.analyticsGenericErrorMessage);
            });
    };

    const handlePeriodChange = (event) => {
        LogService.info(LOG_TAG, "handlePeriodChange", event.target.value);
        setPeriod(event.target.value);
    };

    const handleDcZoneChange = (value) => {
        LogService.info(LOG_TAG, "handleDcZoneChange");
        const index = dcZone.indexOf(value);
        if (index < 0)
            setDcZone([...dcZone, value]);
        else
            setDcZone(dcZone.filter((item) => (item !== value)));
    };

    //Because there is a error with bar graph with 0 value
    const isEmptyGraph = (arr) => {
        let ret = false;
        if (arr && arr.length) {
            ret = arr.map(item => item.data.reduce((a, b) => a + b, 0)).reduce((prev, curr) => prev + curr, 0);
        }
        return (ret === 0);
    }

    LogService.info(LOG_TAG, "render", dcZone);
    const showTurn = false;
    const byDay = (period === "currentday" || period === "previousday" ? true : false);

    let applicationPeriodOptions = AnalyticsConstant.periodOptions.map((period, index) => {
        return (
            <option value={period.value} key={index}>
                {intl.messages[period.value]}
            </option>
        );
    });

    let labelAnalytics = [];
    let labelCumulAnalytics = [];

    let dataGraph = null;
    let dataGraphCumul = null;
    let trafficGraph = null;
    let cumulTrafficGraph = null;
    let turnGraph = null;
    let cumulTurnGraph = null;
    let storageGraph = null;
    let cumulStorageGraph = null;
    let totalCumul = null;

    //=================================    
    if (analytics) {
        const graphAnalytics = analytics.graphData;
        const graphCumulAnalytics = analytics.cumulGraphData;
        labelAnalytics = analytics.graphData.labels;
        labelCumulAnalytics = analytics.cumulGraphData.labels;

        //API USAGE
        dataGraph = {
            series: [
                {
                    name: "Resources API (Unit)",
                    data: graphAnalytics.resources || []
                },
                {
                    name: "Administrative API (Unit)",
                    data: graphAnalytics.administration || []
                }
            ]
        }

        //CUMUL API USAGE
        dataGraphCumul = {
            series: [
                {
                    name: "Resources API (Unit)",
                    data: graphCumulAnalytics.resources || []
                },
                {
                    name: "Administrative API (Unit)",
                    data: graphCumulAnalytics.administration || []
                }
            ]
        }

        //TRAFFIC
        trafficGraph = {
            series: [{
                name: "Web RTC (minutes)",
                data: graphAnalytics.webrtc_minutes ? graphAnalytics.webrtc_minutes.map(value => {
                    return Utils.secondsToMin(value);
                }) : []
            }]
        }

        //CUMUL TRAFFIC
        cumulTrafficGraph = {
            series: [{
                name: "Web RTC (minutes)",
                data: graphCumulAnalytics.webrtc_minutes
                    ? graphCumulAnalytics.webrtc_minutes.map(value => {
                        return Utils.secondsToMin(value);
                    }) : []
            }]
        }

        //TURN
        turnGraph = {
            series: [{
                name: "Turn (Bytes)",
                data: graphAnalytics.turn || []
            }]
        }

        //CUMUL TURN
        cumulTurnGraph = {
            series: [{
                name: "Turn (Bytes)",
                data: graphCumulAnalytics.turn || []
            }]
        }

        //STORAGE
        storageGraph = {
            series: [{
                name: "Storage (Unit)",
                data: graphAnalytics.storage || []
            }]
        }

        //CUMUL STORAGE
        cumulStorageGraph = {
            series: [{
                name: "Storage (Unit)",
                data: graphCumulAnalytics.storage || []
            }]
        }

        totalCumul = {
            totalCumulResources: analytics?.totalCumulResources ?? 0,
            totalCumulAdministration: analytics?.totalCumulAdministration ?? 0,
            totalCumulTraffic: analytics?.totalCumulTraffic ?? 0,
            totalCumulTurn: analytics?.totalCumulTurn ?? 0,
            totalCumulStorage: analytics?.totalCumulStorage ?? 0
        };
    }

    return (
        <React.Fragment>
            {errorMessage.length > 0 &&
                <div>
                    <EmptyState
                        text={intl.messages.analyticsNoDataFound}
                        icon={faChartLine}
                    />
                    <ErrorPanel id="server-error" size={ErrorSize.Medium} className="center">
                        {errorMessage}
                    </ErrorPanel>
                </div>
            }

            {errorMessage.length === 0 && <div>
                {/* Filter */}
                <h6 className="panel-lineTitle mb-3 mt-5"><span>{intl.messages.filters}</span></h6>
                <div className="d-flex align-items-center">
                    <div className="p-2 card-text"><FontAwesomeIcon icon={faFilter} /><span className="ml-2">{intl.messages.period}:</span></div>
                    <select className="form-control w-50"
                        id="analytics-periods-select"
                        value={period}
                        onChange={event => handlePeriodChange(event)}
                    >
                        {applicationPeriodOptions}
                    </select>
                </div>
                <div className="d-flex align-items-center">
                    <div className="p-2 card-text"><FontAwesomeIcon icon={faFilter} /><span className="ml-2">{intl.messages.region}:</span></div>
                    <div className="p-2">
                        <CheckBox
                            id="analytics-dc-eu"
                            checked={dcZone.indexOf(AnalyticsConstant.DC_ZONE.EU) >= 0}
                            label={intl.messages.regionEU}
                            onClick={e => handleDcZoneChange(AnalyticsConstant.DC_ZONE.EU)}
                        /></div>
                    <div className="p-2">
                        <CheckBox
                            id="analytics-dc-na"
                            checked={dcZone.indexOf(AnalyticsConstant.DC_ZONE.NA) >= 0}
                            label={intl.messages.regionNA}
                            onClick={e => handleDcZoneChange(AnalyticsConstant.DC_ZONE.NA)}
                        /></div>
                    <div className="p-2">
                        <CheckBox
                            id="analytics-dc-apac"
                            checked={dcZone.indexOf(AnalyticsConstant.DC_ZONE.APAC) >= 0}
                            label={intl.messages.regionAPAC}
                            onClick={e => handleDcZoneChange(AnalyticsConstant.DC_ZONE.APAC)}
                        /></div>
                </div>

                {/* Title */}
                <div className="row">
                    <div className="col h2 card-title text-center mt-4 mb-3">
                        {period === "currentday" && intl.messages.currentday + " "}
                        {period === "previousday" && intl.messages.previousday + " "}

                        <small className="text-uppercase">{intl.formatMessage({ id: "from" }, { partitifs: byDay ? "de" : "du" })}&nbsp;&nbsp;</small>
                        {Utils.getStartPeriod(period, intl)}

                        <small className="text-uppercase">&nbsp;&nbsp;{intl.formatMessage({ id: "to" }, { partitifs: byDay ? "a" : "au" })}&nbsp;&nbsp;</small>
                        {Utils.getEndPeriod(period, intl)}
                    </div>
                </div>

                {progress &&
                    <EmptyState
                        text=""
                        progress={true}
                    />
                }

                {!progress &&
                    <div>
                        {/* Row with total cumule */}
                        <h6 className="panel-lineTitle mb-3 mt-5"><span>{intl.messages.analyticsCumul}</span></h6>
                        <TotalCumulLine totalCumulData={totalCumul} showTurn={showTurn} />

                        <div className="row">
                            {/* API usage */}
                            {dataGraph && !isEmptyGraph(dataGraph.series) &&
                                <div className="col-lg-6">
                                    <AnalyticsCard
                                        title="API USAGE PER INTERVAL"
                                        canStack={true}
                                        series={dataGraph.series}
                                        xlabel={labelAnalytics}
                                        period={period}
                                        type="bar"
                                    ></AnalyticsCard>
                                </div>
                            }

                            {dataGraphCumul && !isEmptyGraph(dataGraphCumul.series) &&
                                <div className="col-lg-6">
                                    <AnalyticsCard
                                        title="CUMULATIVE API USAGE"
                                        period={period}
                                        xlabel={labelCumulAnalytics}
                                        series={dataGraphCumul.series}
                                        type="area"
                                    ></AnalyticsCard>
                                </div>
                            }


                            {/* Traffic usage */}
                            {trafficGraph && !isEmptyGraph(trafficGraph.series) &&
                                <div className="col-lg-6">
                                    <AnalyticsCard
                                        title="TRAFFIC PER INTERVAL"
                                        period={period}
                                        xlabel={labelAnalytics}
                                        series={trafficGraph.series}
                                        type="bar" />
                                </div>
                            }

                            {cumulTrafficGraph && !isEmptyGraph(cumulTrafficGraph.series) &&
                                <div className="col-lg-6">
                                    <AnalyticsCard
                                        title="CUMULATIVE TRAFFIC USAGE"
                                        period={period}
                                        xlabel={labelCumulAnalytics}
                                        series={cumulTrafficGraph.series}
                                        type="area" />
                                </div>
                            }

                            {/* Turn usage */}
                            {showTurn && turnGraph && !isEmptyGraph(turnGraph.series) &&
                                <div className="col-lg-6">
                                    <AnalyticsCard
                                        title="TURN BYTES PER INTERVAL"
                                        period={period}
                                        xlabel={labelAnalytics}
                                        series={turnGraph.series}
                                        type="bar" />
                                </div>
                            }

                            {showTurn && cumulTurnGraph && !isEmptyGraph(cumulTurnGraph.series) &&
                                <div className="col-lg-6">
                                    <AnalyticsCard
                                        title="CUMULATIVE TURN USAGE"
                                        period={period}
                                        xlabel={labelCumulAnalytics}
                                        series={cumulTurnGraph.series}
                                        type="area" />
                                </div>
                            }

                            {/* Storage usage */}
                            {storageGraph && !isEmptyGraph(storageGraph.series) &&
                                <div className="col-lg-6">
                                    <AnalyticsCard
                                        title="STORAGE USAGE PER INTERVAL"
                                        period={period}
                                        xlabel={labelAnalytics}
                                        series={storageGraph.series}
                                        type="bar" />
                                </div>
                            }

                            {cumulStorageGraph && !isEmptyGraph(cumulStorageGraph.series) &&
                                <div className="col-lg-6">
                                    <AnalyticsCard
                                        title="CUMULATIVE STORAGE USAGE"
                                        period={period}
                                        xlabel={labelCumulAnalytics}
                                        series={cumulStorageGraph.series}
                                        type="area" />
                                </div>
                            }
                        </div>

                        {/* Summary table */}
                        <h6 className="panel-lineTitle mb-3 mt-5"><span>{intl.messages.analyticsTableTitle}</span></h6>
                        <div className="row">
                            <div className="col">
                                <AnalyticsTable analytics={analytics?.data} exportableAnalytics={exportableAnalytics} progress={progress} showTurn={showTurn} />
                            </div>
                        </div>
                    </div>
                }
            </div>}
        </React.Fragment>
    )
}

Analytics.defaultProps = {
}

Analytics.propTypes = {
    appId: PropTypes.string,
    groupsMapping: PropTypes.object
};

const mapDispatchToProps = dispatch => {
    return {
        getAnalytics: (appId, period, fromDay, toDay, options) => dispatch(AnalyticsActions.getAnalytics(appId, period, fromDay, toDay, options))
    }
}

export default connect(null, mapDispatchToProps)(Analytics);